import React from 'react';
import { Modal, Button, Spin } from 'antd';
import { DownloadOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';

interface ViewAnswerSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  answerSheet: {
    id: number;
    name: string;
    imageBase64?: string;
    paperSize?: string;
    questionsNumber?: number;
    numberOfBubbles?: number;
    answerKeys?: string;
  } | null;
  isLoading?: boolean;
}

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
  
  .ant-modal-header {
    padding: 20px 24px 16px;
    border-bottom: none;
    background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  }
  
  .ant-modal-body {
    padding: 0;
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconContainer = styled.div`
  margin-bottom: 12px;
`;

const ViewIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto;
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  box-shadow: 0 6px 16px rgba(82, 196, 26, 0.25);
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #8c8c8c;
  margin: 0;
  line-height: 1.4;
`;

const HeaderInfoSection = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  width: 100%;
`;

const InfoItem = styled.div`
  .label {
    font-size: 11px;
    color: #8c8c8c;
    margin-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f5f5f5;
  min-height: 400px;
  
  img {
    max-width: 100%;
    max-height: 60vh;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  gap: 16px;
  
  .loading-text {
    color: #8c8c8c;
    font-size: 14px;
  }
`;

const NoImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #8c8c8c;
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
  }
  
  .message {
    font-size: 16px;
    font-weight: 500;
  }
  
  .hint {
    font-size: 13px;
    margin-top: 8px;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 20px;
  border-radius: 8px;
  font-weight: 500;
`;

export const ViewAnswerSheetModal: React.FC<ViewAnswerSheetModalProps> = ({
  isOpen,
  onClose,
  answerSheet,
  isLoading = false
}) => {
  const handleDownload = () => {
    if (answerSheet?.imageBase64) {
      // Convert base64 to blob for reliable download to device
      const byteCharacters = atob(answerSheet.imageBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${answerSheet.name || 'answer-sheet'}.png`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <StyledModal
      open={isOpen}
      onCancel={onClose}
      width={800}
      centered
      destroyOnClose
      title={
        <ModalHeader>
          <IconContainer>
            <ViewIcon>
              <EyeOutlined />
            </ViewIcon>
          </IconContainer>
          <Title>{answerSheet?.name || 'Answer Sheet'}</Title>
          <Subtitle>Preview and download your answer sheet</Subtitle>
          {answerSheet && (
            <HeaderInfoSection>
              <InfoItem>
                <div className="label">Paper Size</div>
                <div className="value">{answerSheet.paperSize || 'A4'}</div>
              </InfoItem>
              <InfoItem>
                <div className="label">Questions</div>
                <div className="value">{answerSheet.questionsNumber || '-'}</div>
              </InfoItem>
              <InfoItem>
                <div className="label">Answers</div>
                <div className="value">{answerSheet.numberOfBubbles || '-'}</div>
              </InfoItem>
              <InfoItem>
                <div className="label">Answer Keys</div>
                <div className="value">{answerSheet.answerKeys || '-'}</div>
              </InfoItem>
            </HeaderInfoSection>
          )}
        </ModalHeader>
      }
      footer={
        <ButtonSection>
          <ActionButton onClick={onClose}>
            <CloseOutlined />
            Close
          </ActionButton>
          {answerSheet?.imageBase64 && (
            <ActionButton type="primary" onClick={handleDownload}>
              <DownloadOutlined />
              Download
            </ActionButton>
          )}
        </ButtonSection>
      }
    >
      {isLoading ? (
        <LoadingContainer>
          <Spin size="large" />
          <span className="loading-text">Loading answer sheet...</span>
        </LoadingContainer>
      ) : answerSheet?.imageBase64 ? (
        <ImageContainer>
          <img 
            src={`data:image/png;base64,${answerSheet.imageBase64}`} 
            alt={answerSheet.name || 'Answer Sheet'} 
          />
        </ImageContainer>
      ) : (
        <NoImageContainer>
          <span className="icon">📄</span>
          <span className="message">No preview available</span>
          <span className="hint">The answer sheet image has not been generated yet</span>
        </NoImageContainer>
      )}
    </StyledModal>
  );
};

export default ViewAnswerSheetModal;
